import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api, fmtEUR, BACKEND_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileDown, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const statutBadge = {
  attente: { cls: 'bg-zinc-100 text-zinc-700 border-zinc-300', icon: Clock, label: 'En attente' },
  facture: { cls: 'bg-amber-50 text-amber-800 border-amber-400', icon: AlertCircle, label: 'Facturé' },
  encaisse: { cls: 'bg-emerald-50 text-emerald-800 border-emerald-400', icon: CheckCircle2, label: 'Encaissé' },
};

export default function Factures() {
  const [projets, setProjets] = useState([]);
  const [promoteurs, setPromoteurs] = useState([]);
  const [filterPromo, setFilterPromo] = useState('all');
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [synth, setSynth] = useState(null);
  const [selected, setSelected] = useState(null);
  const [openJalonForm, setOpenJalonForm] = useState(false);
  const [jalonEdit, setJalonEdit] = useState(null);
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i);

  const load = async () => {
    const y = annee === 'all' ? new Date().getFullYear() : Number(annee);
    const [pj, pr, s] = await Promise.all([
      api.get('/projets'),
      api.get('/promoteurs'),
      api.get(`/dashboard/factures-synthese?annee=${y}`),
    ]);
    setProjets(pj.data);
    setPromoteurs(pr.data);
    setSynth(s.data);
  };

  useEffect(() => { load(); }, [annee]);

  const filtered = useMemo(() =>
    projets.filter(p => filterPromo === 'all' || p.promoteur_id === filterPromo)
           .filter(p => !['faisa_cours', 'faisa_ko'].includes(p.statut))
           .filter(p => (p.jalons || []).length > 0),
  [projets, filterPromo]);

  const totals = useMemo(() => {
    let attente = 0, facture = 0, encaisse = 0;
    for (const p of filtered) {
      for (const j of (p.jalons || [])) {
        if (j.statut === 'attente') attente += j.montant || 0;
        else if (j.statut === 'facture') facture += j.montant || 0;
        else if (j.statut === 'encaisse') encaisse += j.montant || 0;
      }
    }
    return { attente, facture, encaisse };
  }, [filtered]);

  const downloadPdf = async (projet, jalon) => {
    try {
      const res = await api.get(`/factures/pdf/${projet.id}/${jalon.id}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Facture_${projet.code}_${jalon.libelle.replace(/\s+/g, '_')}.pdf`;
      link.click();
    } catch (err) {
      toast.error('Erreur génération PDF');
    }
  };

  const updateJalon = async (projet, jalon, updates) => {
    try {
      await api.put(`/projets/${projet.id}/jalons/${jalon.id}`, updates);
      toast.success('Jalon mis à jour');
      load();
    } catch (err) { toast.error('Erreur'); }
  };

  return (
    <Layout
      title="Facturation"
      subtitle={`Jalons & factures — ${annee === 'all' ? 'toutes années' : annee}`}
      actions={
        <div className="flex gap-2">
          <Select value={annee} onValueChange={setAnnee}>
            <SelectTrigger className="w-28 rounded-none border-zinc-900" data-testid="factures-annee"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterPromo} onValueChange={setFilterPromo}>
            <SelectTrigger className="w-56 rounded-none border-zinc-900"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">Tous promoteurs</SelectItem>
              {promoteurs.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      }
    >
      {/* Zone synthèse année */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 border-l-4 border-l-[#002FA7] p-5" data-testid="facture-merite">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] mb-2">Mérité {annee}</div>
          <div className="font-mono font-medium text-2xl">{fmtEUR(synth?.merite || 0)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{synth?.nb_jalons_merite || 0} jalons facturés</div>
        </div>
        <div className="bg-white border border-zinc-200 border-l-4 border-l-emerald-500 p-5" data-testid="facture-encaisse">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mb-2">Encaissé {annee}</div>
          <div className="font-mono font-medium text-2xl text-emerald-800">{fmtEUR(synth?.encaisse || 0)}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{synth?.nb_jalons_encaisse || 0} jalons encaissés</div>
        </div>
        <div className="bg-white border border-zinc-200 border-l-4 border-l-amber-400 p-5" data-testid="facture-reste">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 mb-2">Reste à encaisser</div>
          <div className="font-mono font-medium text-2xl text-amber-800">{fmtEUR((synth?.merite || 0) - (synth?.encaisse || 0))}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Mérité – encaissé</div>
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Taux encaissement</div>
          <div className="font-mono font-medium text-2xl">
            {synth?.merite ? `${Math.round((synth.encaisse / synth.merite) * 100)}%` : '—'}
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">Sur l'année</div>
        </div>
      </div>

      {/* Totaux par statut de jalon (tous projets visibles) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">En attente</div>
          <div className="font-mono font-medium text-3xl">{fmtEUR(totals.attente)}</div>
        </div>
        <div className="bg-white border border-zinc-200 border-l-4 border-l-amber-400 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 mb-2">Facturé</div>
          <div className="font-mono font-medium text-3xl text-amber-800">{fmtEUR(totals.facture)}</div>
        </div>
        <div className="bg-white border border-zinc-200 border-l-4 border-l-emerald-500 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mb-2">Encaissé</div>
          <div className="font-mono font-medium text-3xl text-emerald-800">{fmtEUR(totals.encaisse)}</div>
        </div>
      </div>

      <div className="space-y-6" data-testid="factures-projets">
        {filtered.map((projet) => (
          <div key={projet.id} className="bg-white border border-zinc-200">
            <div className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-zinc-500">{projet.code}</div>
                <div className="font-display font-bold text-lg tracking-tight">{projet.nom}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total honoraires</div>
                <div className="font-mono font-medium text-xl text-[#002FA7]">
                  {fmtEUR((projet.jalons || []).reduce((s, j) => s + (j.montant || 0), 0))}
                </div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="px-6 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Jalon</th>
                  <th className="px-6 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">%</th>
                  <th className="px-6 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Montant HT</th>
                  <th className="px-6 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Statut</th>
                  <th className="px-6 py-2 text-left text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Dates</th>
                  <th className="px-6 py-2 text-right text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(projet.jalons || []).map((j) => {
                  const sb = statutBadge[j.statut] || statutBadge.attente;
                  const Icon = sb.icon;
                  return (
                    <tr key={j.id} className="border-b border-zinc-100">
                      <td className="px-6 py-3 font-medium">{j.libelle}</td>
                      <td className="px-6 py-3 font-mono text-right">{j.pourcentage}%</td>
                      <td className="px-6 py-3 font-mono text-right font-medium">{fmtEUR(j.montant || 0)}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border ${sb.cls}`}>
                          <Icon size={10} /> {sb.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-xs font-mono text-zinc-600">
                        {j.date_facturation && <div>Fact. {j.date_facturation}</div>}
                        {j.date_encaissement && <div>Enc. {j.date_encaissement}</div>}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <div className="flex gap-1 justify-end items-center">
                          <Select value={j.statut || 'attente'} onValueChange={(v) => {
                            const upd = { statut: v };
                            if (v === 'facture' && !j.date_facturation) upd.date_facturation = new Date().toISOString().slice(0, 10);
                            if (v === 'encaisse' && !j.date_encaissement) upd.date_encaissement = new Date().toISOString().slice(0, 10);
                            updateJalon(projet, j, upd);
                          }}>
                            <SelectTrigger className="rounded-none w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="rounded-none">
                              <SelectItem value="attente">En attente</SelectItem>
                              <SelectItem value="facture">Facturé</SelectItem>
                              <SelectItem value="encaisse">Encaissé</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" className="rounded-none border-zinc-900 h-8" onClick={() => downloadPdf(projet, j)} data-testid={`pdf-facture-${projet.code}-${j.id}`}>
                            <FileDown size={12} className="mr-1" /> PDF
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white border border-zinc-200 p-16 text-center text-zinc-500">
            Aucun projet avec jalons. Ajoutez des jalons depuis la page Projets.
          </div>
        )}
      </div>
    </Layout>
  );
}
